/**
 * Eindeutige Geräte-ID-Generierung
 * 
 * Diese Funktion generiert eine eindeutige, persistente Geräte-ID für das aktuelle Gerät.
 * Die ID wird in localStorage gespeichert und bleibt über Browser-Sessions hinweg erhalten.
 * 
 * Strategien (in Reihenfolge der Priorität):
 * 1. localStorage-basierte UUID (primär)
 * 2. Browser-Fingerprinting als Fallback
 * 3. Hostname-basierte ID als letzter Fallback
 */

const DEVICE_ID_KEY = 'stoppuhr_device_id';
const CUSTOM_DEVICE_NAME_KEY = 'stoppuhr_custom_device_name';
const DEVICE_ID_VERSION = '1.0'; // Für zukünftige Migrationen

/**
 * Generiert eine UUID v4 mit Web Crypto API (falls verfügbar)
 * @returns {string} UUID v4
 */
function generateUUID() {
  // Moderne Browser: Web Crypto API
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback: crypto.getRandomValues
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    
    // Setze Version (4) und Variant (10)
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10
    
    // Konvertiere zu UUID-String
    const hex = Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20, 32)
    ].join('-');
  }
  
  // Letzter Fallback: Math.random (nicht kryptografisch sicher, aber funktional)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/**
 * Generiert einen Browser-Fingerprint als Fallback
 * Kombiniert verschiedene Browser-Eigenschaften zu einer eindeutigen ID
 * @returns {string} Fingerprint-Hash
 */
function generateFingerprint() {
  const components = [];
  
  try {
    // User Agent
    if (navigator.userAgent) {
      components.push(navigator.userAgent);
    }
    
    // Sprache
    if (navigator.language) {
      components.push(navigator.language);
    }
    if (navigator.languages && navigator.languages.length > 0) {
      components.push(navigator.languages.join(','));
    }
    
    // Plattform
    if (navigator.platform) {
      components.push(navigator.platform);
    }
    
    // Hardware-Concurrency
    if (navigator.hardwareConcurrency) {
      components.push(navigator.hardwareConcurrency.toString());
    }
    
    // Max Touch Points
    if (navigator.maxTouchPoints) {
      components.push(navigator.maxTouchPoints.toString());
    }
    
    // Screen-Eigenschaften
    if (window.screen && window.screen.width && window.screen.height) {
      components.push(`${window.screen.width}x${window.screen.height}`);
    }
    if (window.screen && window.screen.colorDepth) {
      components.push(window.screen.colorDepth.toString());
    }
    if (window.screen && window.screen.pixelDepth) {
      components.push(window.screen.pixelDepth.toString());
    }
    
    // Timezone
    if (Intl && Intl.DateTimeFormat) {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        components.push(timezone);
      } catch (e) {
        // Ignoriere Fehler
      }
    }
    
    // Canvas-Fingerprint (vereinfacht)
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('StoppUhr-Device-ID', 2, 2);
        const canvasData = canvas.toDataURL();
        // Verwende nur einen Teil des Canvas-Daten für Performance
        components.push(canvasData.substring(0, 50));
      }
    } catch (e) {
      // Canvas nicht verfügbar
    }
    
  } catch (e) {
    console.warn('[DeviceID] Fehler beim Generieren des Fingerprints:', e);
  }
  
  // Erstelle Hash aus allen Komponenten
  const combined = components.join('|');
  
  // Einfacher Hash (djb2-Algorithmus)
  let hash = 5381;
  for (let i = 0; i < combined.length; i++) {
    hash = ((hash << 5) + hash) + combined.charCodeAt(i);
    hash = hash & hash; // Konvertiere zu 32-bit Integer
  }
  
  // Konvertiere zu Hex-String (16 Zeichen)
  return Math.abs(hash).toString(16).padStart(16, '0');
}

/**
 * Lädt oder generiert eine eindeutige Geräte-ID
 * @returns {string} Eindeutige Geräte-ID
 */
export function getDeviceId() {
  try {
    // Versuche gespeicherte ID zu laden
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.id && data.version === DEVICE_ID_VERSION) {
          return data.id;
        }
        // Alte Version oder ungültiges Format - generiere neue ID
      } catch (e) {
        // Altes Format (nur String) - migriere zu neuem Format
        if (typeof stored === 'string' && stored.length > 0) {
          const migrated = {
            id: stored,
            version: DEVICE_ID_VERSION,
            created: new Date().toISOString(),
            migrated: true
          };
          localStorage.setItem(DEVICE_ID_KEY, JSON.stringify(migrated));
          return stored;
        }
      }
    }
    
    // Keine gespeicherte ID gefunden - generiere neue
    let deviceId;
    
    // Versuche UUID zu generieren
    try {
      deviceId = generateUUID();
    } catch (e) {
      console.warn('[DeviceID] UUID-Generierung fehlgeschlagen, verwende Fingerprint:', e);
      // Fallback: Fingerprint
      deviceId = `fp-${generateFingerprint()}`;
    }
    
    // Speichere ID mit Metadaten
    const deviceData = {
      id: deviceId,
      version: DEVICE_ID_VERSION,
      created: new Date().toISOString(),
      userAgent: navigator.userAgent || 'unknown',
      platform: navigator.platform || 'unknown'
    };
    
    localStorage.setItem(DEVICE_ID_KEY, JSON.stringify(deviceData));
    
    console.log('[DeviceID] Neue Geräte-ID generiert:', deviceId);
    
    return deviceId;
    
  } catch (e) {
    console.error('[DeviceID] Fehler beim Laden/Generieren der Geräte-ID:', e);
    
    // Letzter Fallback: Hostname-basierte ID
    try {
      if (typeof window !== 'undefined' && window.location) {
        const hostname = window.location.hostname;
        if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
          return `host-${hostname.split('.')[0]}-${Date.now()}`;
        }
      }
    } catch (e2) {
      // Ignoriere
    }
    
    // Absoluter Fallback
    return `device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}

/**
 * Gibt den Device-Namen zurück (für Anzeige)
 * Verwendet benutzerdefinierten Namen, falls gesetzt, sonst generiert einen aus der ID
 * @returns {string} Device-Name
 */
export function getDeviceName() {
  try {
    // Prüfe auf benutzerdefinierten Namen
    const customName = localStorage.getItem(CUSTOM_DEVICE_NAME_KEY);
    if (customName && customName.trim()) {
      return customName.trim();
    }
  } catch (e) {
    // Ignoriere Fehler
  }
  
  // Kein benutzerdefinierter Name - generiere aus ID
  const deviceId = getDeviceId();
  
  try {
    // Wenn es eine UUID ist, verwende die ersten 8 Zeichen
    if (deviceId.includes('-') && deviceId.length === 36) {
      return `Device-${deviceId.substring(0, 8).toUpperCase()}`;
    }
    
    // Wenn es ein Fingerprint ist
    if (deviceId.startsWith('fp-')) {
      return `Device-${deviceId.substring(3, 11).toUpperCase()}`;
    }
    
    // Wenn es hostname-basiert ist
    if (deviceId.startsWith('host-')) {
      return deviceId.replace('host-', '').split('-')[0] || 'Device';
    }
    
    // Standard: Erste 8 Zeichen
    return `Device-${deviceId.substring(0, 8).toUpperCase()}`;
    
  } catch (e) {
    return 'Device-Unknown';
  }
}

/**
 * Setzt einen benutzerdefinierten Device-Namen
 * @param {string} customName - Benutzerdefinierter Name
 */
export function setCustomDeviceName(customName) {
  try {
    if (customName && customName.trim()) {
      localStorage.setItem(CUSTOM_DEVICE_NAME_KEY, customName.trim());
      console.log('[DeviceID] Benutzerdefinierter Device-Name gesetzt:', customName.trim());
    } else {
      // Leerer String = entferne benutzerdefinierten Namen
      localStorage.removeItem(CUSTOM_DEVICE_NAME_KEY);
      console.log('[DeviceID] Benutzerdefinierter Device-Name entfernt');
    }
  } catch (e) {
    console.error('[DeviceID] Fehler beim Setzen des benutzerdefinierten Namens:', e);
  }
}

/**
 * Gibt den benutzerdefinierten Device-Namen zurück (oder null)
 * @returns {string|null} Benutzerdefinierter Name oder null
 */
export function getCustomDeviceName() {
  try {
    const customName = localStorage.getItem(CUSTOM_DEVICE_NAME_KEY);
    return customName && customName.trim() ? customName.trim() : null;
  } catch (e) {
    return null;
  }
}

/**
 * Gibt Metadaten zur Device-ID zurück
 * @returns {object} Metadaten
 */
export function getDeviceIdMetadata() {
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignoriere
  }
  return null;
}

/** Regex: 1–3 Ziffern (IP-Oktett / Hostname-Fallback wie "192") */
const IP_OCTET_FALLBACK_REGEX = /^[0-9]{1,3}$/;

/**
 * Prüft, ob ein String ein IP-Oktett-Fallback ist (z. B. "192" von hostname).
 * @param {string} str - Zu prüfender String
 * @returns {boolean}
 */
export function isIpOctetFallback(str) {
  return typeof str === 'string' && IP_OCTET_FALLBACK_REGEX.test(str.trim());
}

/**
 * Ersetzt in sessionName den Klammer-Inhalt am Ende, wenn er ein IP-Oktett-Fallback ist.
 * @param {string} sessionName - z. B. "Messung 31.01.2025 (192)"
 * @param {string} currentDeviceName - z. B. "device01"
 * @returns {string} Anzeigename, z. B. "Messung 31.01.2025 (device01)"
 */
function replaceSessionNameOctetWith(sessionName, currentDeviceName) {
  if (!sessionName || typeof sessionName !== 'string' || !currentDeviceName) return sessionName;
  const match = sessionName.match(/\(([^)]*)\)\s*$/);
  if (!match) return sessionName;
  const inParens = match[1].trim();
  if (!isIpOctetFallback(inParens)) return sessionName;
  return sessionName.replace(/\s*\([^)]*\)\s*$/, ` (${currentDeviceName})`);
}

/**
 * Gibt den Session-Namen für die Anzeige zurück.
 * Ersetzt (192) o. ä. durch den aktuellen Gerätenamen, wenn ein Fallback erkannt wird.
 * @param {Object|null} session - Session mit sessionName / deviceName
 * @param {string} currentDeviceName - Aktueller Gerätename (z. B. aus Konfiguration)
 * @returns {string} Anzeigename
 */
export function getDisplaySessionName(session, currentDeviceName) {
  if (!session) return '';
  const raw = session.sessionName || '';
  if (!currentDeviceName) return raw;
  // Session.deviceName ist IP-Oktett → Anzeige mit aktuellem Gerätenamen
  if (isIpOctetFallback(session.deviceName)) {
    return replaceSessionNameOctetWith(raw, currentDeviceName);
  }
  // Klammer-Inhalt in sessionName ist IP-Oktett
  return replaceSessionNameOctetWith(raw, currentDeviceName);
}

/**
 * Migriert eine Session: deviceName/sessionName von IP-Oktett-Fallback auf currentDeviceName.
 * Nur anwenden, wenn ein benutzerdefinierter Gerätename gesetzt ist (getCustomDeviceName()).
 * @param {Object} session - Session-Objekt (wird nicht mutiert)
 * @param {string} currentDeviceName - Aktueller Gerätename
 * @returns {{ session: Object, migrated: boolean }} Migrierte Session und Flag
 */
export function migrateSessionDeviceName(session, currentDeviceName) {
  if (!session || !currentDeviceName) return { session, migrated: false };
  const deviceIsFallback = isIpOctetFallback(session.deviceName);
  const name = session.sessionName || '';
  const match = name.match(/\s*\(([^)]*)\)\s*$/);
  const parensIsFallback = match && isIpOctetFallback(match[1].trim());
  if (!deviceIsFallback && !parensIsFallback) return { session, migrated: false };
  const updated = { ...session };
  if (deviceIsFallback) updated.deviceName = currentDeviceName;
  if (parensIsFallback) updated.sessionName = replaceSessionNameOctetWith(name, currentDeviceName);
  return { session: updated, migrated: true };
}
